import React from 'react';
import CustomiseTable from '../../components/CustomiseTable';
import { Box, Button, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material';
import { SERVICE_STATUS } from '../../contants/PrimeAccountServicesPageConstant';
import { CustomChip } from '../../components/CustomChip';
import TableList from '../../components/TableList';
import { format } from 'date-fns';
import CustomTooltip from '../../components/CustomTooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { post } from '../../api';
import { SUPPORT_TYPE_LIST } from '../../contants';

const successStatus = ['Active', 'Sent to Customer'];
const errorStatus = ['Needs Attention', 'Inactive'];

function formatStatusText(text) {
    if (!text) return '';
    return text
        .toLowerCase()
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

function PrimeServicesTableAndList({ tableBodyRowList, userDetails, name, companyId }) {
    const theme = useTheme();
    const isMobileView = useMediaQuery(theme.breakpoints.down('sm'));

    const firstRow = tableBodyRowList?.[0];
    const relatedCompany = firstRow?.companyId ? userDetails?.contact?.relatedAccounts?.find((item) => item.accountId === firstRow.companyId) : null;
    const companyUrl = relatedCompany?.id ? `/my-companies/${relatedCompany.id}` : '';

    const handleHeadingClick = (event) => {
        if (!companyUrl) {
            return;
        }
        event.preventDefault();

        // window.open(companyUrl, '_blank', 'noopener,noreferrer');

        const navEvent = new CustomEvent('navigate', {
            detail: { url: companyUrl },
        });
        document.dispatchEvent(navEvent);
    };

    const getTableSettings = () => {
        return [
            {
                id: 'services',
                name: 'Services',
                renderElement: (row) => {
                    return (
                        <>
                            <Box display={'flex'} alignItems={'start'}>
                                <Typography component={'span'} sx={{ fontSize: '14px', fontWeight: '600' }}>
                                    {row.name && row.name.startsWith('Estate Planning') ? 'Estate Planning' : row.name}
                                </Typography>
                            </Box>
                        </>
                    );
                },
            },
            {
                id: 'fulfillingCompany',
                name: 'Fulfillment Company',
                hideCellValue: true,
                renderElement: (row) => {
                    return (
                        <>
                            {row.fulfillingCompany && (
                                <Typography
                                    // gutterBottom
                                    variant="paraText5"
                                    color="text.neutralSecondary"
                                >
                                    {row.fulfillingCompany}
                                </Typography>
                            )}
                        </>
                    );
                },
            },
            {
                id: 'status',
                name: 'Status',
                hideCellValue: true,
                rightCellSx: {
                    flexDirection: 'row',
                    alignItems: 'center',
                },
                tableBodyCellSx: {
                    whiteSpace: 'nowrap',
                },
                renderElement: (row) => {
                    return (
                        <>
                            {row?.customerFacingStatus && (
                                <CustomChip
                                    variant="outlined"
                                    color={successStatus.indexOf(row?.customerFacingStatus) > -1 ? 'success' : errorStatus.indexOf(row?.customerFacingStatus) > -1 ? 'error' : 'warning'}
                                    label={formatStatusText(row?.customerFacingStatus)}
                                    style={row?.customerFacingStatus === 'Inactive' ? { border: `1px solid lightgray` } : {}}
                                    sx={(theme) => ({
                                        ...(row?.customerFacingStatus === SERVICE_STATUS?.notfiled
                                            ? {
                                                  border: `1px solid ${theme.palette.text.tertiary}`,
                                                  background: 'transparent',
                                              }
                                            : row?.customerFacingStatus === SERVICE_STATUS?.suggested
                                              ? {
                                                    border: `1px solid ${theme.palette.border.neutralSecondary}`,
                                                    background: 'transparent',
                                                }
                                              : null),
                                    })}
                                />
                            )}
                            {row?.description && (
                                <CustomTooltip title={row.description}>
                                    <IconButton
                                        sx={(theme) => ({
                                            marginLeft: '0.25rem',
                                            color: 'gray.900',
                                            padding: 0,

                                            // '& svg': {
                                            //     width: '18px',
                                            //     height: '18px',
                                            // },
                                        })}
                                    >
                                        <InfoOutlinedIcon fontSize="small" />
                                    </IconButton>
                                </CustomTooltip>
                            )}
                        </>
                    );
                },
            },
            {
                id: 'actions',
                name: <span style={{ marginLeft: '1rem' }}>Action</span>,
                hideLeftCell: true,
                rightCellSx: {
                    width: {
                        xs: '100%',
                        sm: 'unset',
                    },
                },
                renderElement: (row) => {
                    return (
                        <Box
                            sx={(theme) => ({
                                display: 'flex',
                                flexDirection: { xs: 'row', sm: 'column' },
                                justifyContent: { xs: row?.actions?.length === 1 ? 'center' : 'space-between', sm: 'flex-start' },
                                alignItems: 'flex-start',

                                width: {
                                    xs: '100%',
                                    sm: 'unset',
                                },

                                borderTop:
                                    row.customerFacingStatus !== 'Inactive' && getAction(row)?.length > 0
                                        ? { xs: `1px solid ${theme.palette.grey[300]}`, sm: 'none' }
                                        : {
                                              xs: 'unset',
                                              sm: 'unset',
                                          },
                                marginTop: { xs: theme.typography.generic.xxxSmall, sm: '0px' },
                                paddingTop: { xs: theme.typography.generic.xxxSmall, sm: '0px' },
                            })}
                        >
                            {row.customerFacingStatus !== 'Inactive' && (
                                <>
                                    {getAction(row)?.map((cBtn) => (
                                        <Button size="small" variant="text" key={cBtn} color="primary" sx={{ textTransform: 'none', fontSize: '14px' }} onClick={() => performAction(row, cBtn)}>
                                            {cBtn}
                                        </Button>
                                    ))}
                                </>
                            )}
                        </Box>
                    );
                },
            },
        ];
    };

    const getCompanyName = (row) => {
        if (!row) {
            return '';
        }
        if (row.name === 'Tax Preparation' || row.name === 'Tax Planning' || row.name.startsWith('Estate Planning')) {
            return row.fullLegalName;
        }
        return row.finalizedBusinessName ? row.finalizedBusinessName : row.companyName;
    };

    const getSupportList = (row) => {
        return SUPPORT_TYPE_LIST[row.name];
    };

    const performAction = (row, action) => {
        if (action === 'Schedule call') {
            window.open(process.env.REACT_APP_CALENDLY_URL, '_blank').focus();
        } else if (action === 'Manage entity') {
            let company = userDetails.contact.relatedAccounts?.find((item) => item.accountId === row.companyId);
            const event = new CustomEvent('navigate', {
                detail: { url: '/my-companies/' + company?.id },
            });

            document.dispatchEvent(event);
        } else if (action === 'Update payment info') {
            const event = new CustomEvent('navigate', {
                detail: { url: '/payment-information/payment-methods' },
            });

            document.dispatchEvent(event);
        } else if (action === 'Request support') {
            let company = userDetails.contact.relatedAccounts?.find((item) => item.accountId === row.companyId);

            if (
                row.name === 'Entity Formation' ||
                row.name === 'Registered Agent' ||
                row.name === 'Branded Domain & Email' ||
                row.name === 'Bankable Website' ||
                row.name === 'Estate Planning (PRIME)' ||
                row.name === 'Estate Planning (Unlimited)' ||
                row.name === 'Professional Registered Agent'
            ) {
                createCase({
                    title: `Request support—${row?.name}`, //Request Support—[Service Label]
                    subTitle: 'Tell us a little more about how we can help you and an agent will contact you shortly!',
                    subject: `Request support: ${row?.name}`,
                    companyName: getCompanyName(row),
                    recordType: 'General Case',
                    accountId: company?.accountId,

                    hideCompanyField: false,
                    showCustomerField: Boolean(getSupportList(row)?.length || 0),
                    customerLabelList: getSupportList(row),
                });
            } else {
                createCase({
                    title: `Request support—${row?.name}`, //Request Support—[Service Label]
                    subTitle: 'Tell us a little more about how we can help you and an agent will contact you shortly!',
                    subject: `Request support: ${row?.name}`,
                    companyName: getCompanyName(row),
                    recordType: 'General Case',
                    accountId: company?.accountId,
                });
            }
        } else if (action === 'View mailbox') {
            const event = new CustomEvent('navigate', {
                detail: { url: `/mail?filterId=1&filter=${row.companyId}` },
            });

            document.dispatchEvent(event);
        } else if (action === 'Go to INCITE') {
            window.open('https://iris.incitetax.com/', '_blank').focus();
        } else if (action === 'INCITE support') {
            window.open('https://www.incite.com/Welcome?ReturnUrl=%2f', '_blank').focus();
        } else if (action === 'View tax docs') {
            let company = userDetails.contact.relatedAccounts?.find((item) => item.accountId === row.companyId);
            const event = new CustomEvent('navigate', {
                detail: { url: '/my-companies/' + company?.id },
            });

            document.dispatchEvent(event);
        } else if (action === 'FincenFetch portal') {
            window.open('https://app.fincenfetch.com/login', '_blank').focus();
        } else if (action === 'View BOI filing doc') {
            let company = userDetails.contact.relatedAccounts?.find((item) => item.accountId === row.companyId);
            const event = new CustomEvent('navigate', {
                detail: { url: '/my-companies/' + company?.id },
            });

            document.dispatchEvent(event);
        } else if (action === 'Go to Fundability') {
            post('custom/getFundabilityLogin', { id: row.opportunityId }).then((result) => {
                if (result.isSuccess) {
                    if (result.data.link) {
                        window.open(result.data.link, '_blank').focus();
                    } else {
                        let messageDetail = { variant: 'error', message: result.data.error };
                        const event = new CustomEvent('openToster', {
                            detail: messageDetail,
                        });
                        document.dispatchEvent(event);
                    }
                }
            });
        } else if (action === 'View Plan') {
            post('custom/getDecoderFinalDeliverable', { id: row.opportunityId }).then((result) => {
                if (result.isSuccess) {
                    if (result.data.link) {
                        window.open(result.data.link, '_blank').focus();
                    } else {
                        let messageDetail = { variant: 'error', message: result.data.error };
                        const event = new CustomEvent('openToster', {
                            detail: messageDetail,
                        });
                        document.dispatchEvent(event);
                    }
                }
            });
        } else if (action === 'Fundability support') {
            window.open('http://fundability.com/contact-us/', '_blank').focus();
        } else if (action === 'Estate Guru portal') {
            window.open('https://my.estateguru.com/login', '_blank').focus();
        } else if (action === 'Go to Relay') {
            window.open('https://app.relayfi.com/login', '_blank').focus();
        } else if (action === 'Go to my portal') {
            window.open('https://estateplan.primecorporateservices.com/', '_blank').focus();
        } else if (action === 'View website') {
            let url = row.finalizedDomainName?.startsWith('http') ? row.finalizedDomainName : row.finalizedDomainName ? `https://${row.finalizedDomainName}` : '';

            window.open(url, '_blank').focus();
        } else if (action === 'Relay support') {
            window.open('http://support.relayfi.com', '_blank').focus();
        } else if (action === 'Complete Survey') {
            window.open(process.env.REACT_APP_TAX_DECODER_URL, '_blank').focus();
        }
    };
    const createCase = (detail) => {
        const event = new CustomEvent('createCase', {
            detail: detail,
        });

        document.dispatchEvent(event);
    };
    const getAction = (row) => {
        let actions = [];
        if (row.name === 'Entity Formation') {
            actions.push('Manage entity');
            actions.push('Request support');
            actions.push('Update payment info');
        } else if (row.name === 'Registered Agent') {
            actions.push('View mailbox');
            actions.push('Request support');
            actions.push('Update payment info');
        } else if (row.name === 'Professional Registered Agent') {
            actions.push('View mailbox');
            actions.push('Request support');
            actions.push('Update payment info');
        } else if (row.name === 'Tax Preparation') {
            if (row.portalReady) {
                actions.push('Go to INCITE');
            }
            // actions.push('Request support');
        } else if (row.name === 'Tax Planning') {
            if (row.customerFacingStatus?.toLowerCase() === 'needs attention') {
                actions.push('Complete Survey');
            }
            if (row.customerFacingStatus?.toLowerCase() === 'active') {
                actions.push('View Plan');
            }
            actions.push('Request support');
        } else if (row.name === 'BOI Report Filing') {
            if (row.portalReady) {
                actions.push('FincenFetch portal');
            }
            actions.push('View BOI filing doc');
        } else if (row.name === 'Business Credit') {
            if (row.portalReady) {
                actions.push('Go to Fundability');
            }
            actions.push('Fundability support');
        } else if (row.name === 'Business Banking') {
            if (row.portalReady) {
                actions.push('Go to Relay');
            }

            actions.push('Relay support');
        } else if (row.name === 'Estate Planning (PRIME)') {
            if (row.portalReady) {
                actions.push('Go to my portal');
            }

            actions.push('Request support');
        } else if (row.name === 'Estate Planning (Unlimited)') {
            actions.push('Request support');
        } else if (row.name === 'Branded Domain & Email') {
            actions.push('Request support');
            actions.push('Update payment info');
        } else if (row.name === 'Bankable Website') {
            if (row.portalReady && row.finalizedDomainName) {
                actions.push('View website');
            }
            actions.push('Request support');
            actions.push('Update payment info');
        }

        return actions;
    };

    const showHeadingTextUI = companyUrl ? (
        <Box
            component="span"
            role="link"
            tabIndex={0}
            onClick={handleHeadingClick}
            onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    handleHeadingClick(event);
                }
            }}
            sx={{
                cursor: 'pointer',
                textDecoration: 'none',
                textUnderlineOffset: '2px',
                '&:hover': {
                    textDecoration: 'underline',
                },
            }}
        >
            {name}
        </Box>
    ) : (
        name
    );

    return (
        <>
            {!isMobileView ? (
                <CustomiseTable
                    columns={getTableSettings()}
                    rows={tableBodyRowList}
                    hideHeader={true}
                    showHeading={true}
                    headingText={showHeadingTextUI}
                    componentStyleProps={{
                        tableContainerSx: {
                            color: theme.palette.text.secondary,
                            fontSize: theme.typography.customFontSize.large, // 14px
                            fontWeight: theme.typography.fontWeightRegular,
                            lineHeight: '150%', // Line height as a percentage of font size

                            background: theme.palette.background.paper,
                            borderRadius: theme.typography.borderRadius.xSmall,
                            border: `1px solid ${theme.palette.border.neutralPrimary}`,
                        },
                        tableSx: {
                            color: theme.palette.text.secondary,
                            fontWeight: theme.typography.fontWeightRegular,
                            lineHeight: '150%', // Line height as a percentage of font size
                            fontSize: theme.typography.customFontSize.large, // 14px
                        },
                        tableHeaderCellSx: {
                            color: theme.palette.text.primary,
                            fontSize: theme.typography.customFontSize.medium, // 16px
                            fontWeight: theme.typography.fontWeightSemiBold,
                            lineHeight: theme.typography.customFontSize.large,
                            // minWidth: 200,
                            width: 200,
                        },
                        tableBodyCellSx: {
                            background: theme.palette.background.paper,
                            color: theme.palette.text.primary,
                            borderColor: theme.palette.grey[300],
                            fontSize: theme.typography.customFontSize.small, // 14px
                            fontWeight: theme.typography.fontWeightMedium,
                            lineHeight: '150%',
                            width: '300px',
                        },
                    }}
                />
            ) : (
                <TableList showHeading={true} headingText={showHeadingTextUI} columns={getTableSettings()} rows={tableBodyRowList} />
            )}
        </>
    );
}

export default PrimeServicesTableAndList;
